import type { GqlAsset, GqlTemplate, GqlTemplateContributor } from './client-gql/types.ts';

type AssetEnvelope = {
	fields: {
		title: string;
		description: string;
		file: { url: string; title: string };
	};
} | null;

const toAssetEnvelope = (asset: GqlAsset | null | undefined): AssetEnvelope =>
	asset
		? {
				fields: {
					title: asset.title ?? '',
					description: asset.description ?? '',
					file: {
						url: asset.url ?? '',
						title: asset.title ?? '',
					},
				},
			}
		: null;

const toAssetEnvelopeArray = (
	assets: (GqlAsset | null | undefined)[] | undefined,
): NonNullable<AssetEnvelope>[] => (assets ?? []).map(toAssetEnvelope).filter(isPresent);

const isPresent = <T>(value: T | null | undefined): value is T =>
	value !== null && value !== undefined;

/**
 * Maps the contributor union into the same resolved shape the SSR/Uniform fetch
 * produces, so search resolves the same `fields.contributor.*` paths on
 * backfilled and seed items.
 */
function mapContributor(contributor: GqlTemplateContributor | undefined): unknown {
	if (!contributor) return null;
	if (contributor.__typename === 'Author') {
		return {
			fields: {
				authorNameExternal: contributor.authorNameExternal ?? '',
				organization: contributor.organization
					? {
							fields: {
								organizationNameExternal: contributor.organization.organizationNameExternal ?? '',
							},
						}
					: null,
			},
		};
	}
	if (contributor.__typename === 'CompanyNameInternal') {
		return {
			fields: {
				organizationNameExternal: contributor.organizationNameExternal ?? '',
			},
		};
	}
	return null;
}

export function mapTemplateItem(item: GqlTemplate): unknown {
	const categories = (item.categoryCollection?.items ?? []).filter(isPresent).map((cat) => ({
		sys: { id: cat.sys.id },
		fields: {
			urlSlug: cat.urlSlug ?? '',
			categoryNameExternal: cat.categoryNameExternal ?? '',
			shortDescriptionOneLiner: cat.shortDescriptionOneLiner ?? '',
		},
	}));

	const products = (item.productCollection?.items ?? []).filter(isPresent).map((prod) => ({
		sys: { id: prod.sys.id },
		fields: {
			internalTitle: prod.internalTitle ?? '',
			url: prod.url ?? '',
			productNameReference: {
				fields: {
					productName: prod.productNameReference?.productName ?? '',
				},
			},
			icon: toAssetEnvelope(prod.icon),
		},
	}));

	return {
		sys: { id: item.sys.id },
		fields: {
			templateNameInternal: item.templateNameInternal ?? '',
			templateNameExternal: item.templateNameExternal ?? '',
			oneLinerHeadline: item.oneLinerHeadline ?? '',
			fullUrlSlug: item.fullUrlSlug ?? '',
			templateIcon: toAssetEnvelope(item.templateIcon),
			templateThumbnail: toAssetEnvelope(item.templateThumbnail),
			templatePreview: toAssetEnvelopeArray(item.templatePreviewCollection?.items),
			contributor: mapContributor(item.contributor),
			category: categories,
			product: products,
		},
	};
}
